import type {
  ScorecardIncident,
  ScorecardMonitorIssue,
  ScorecardMonitorRun,
} from '../../common/types';

export type SourceTiming = {
  source: string;
  issues: number;
  attempted: number;
  success: number;
  failed: number;
  degraded: number;
  pending: number;
  duration_ms: number | null;
};

export type RunMetrics = {
  sources: SourceTiming[];
  overall: {
    businesses_scanned?: number;
    reports_scanned?: number;
    healthy_reports?: number;
    issues_found?: number;
    incidents_opened?: number;
    duration_ms?: number;
  } | null;
  probes: {
    total?: number;
    completed?: number;
    failed?: number;
    degraded?: number;
    pending?: number;
    timed_out?: boolean;
  } | null;
  businesses_due: number | null;
  recrawls_queued: number | null;
};

export type DailyRunStat = {
  date: string;
  total: number;
  completed: number;
  failed: number;
  running: number;
  zeroBusinessCompleted: number;
  successRate: number | null;
};

export type MonitorHealth = {
  status: 'online' | 'degraded' | 'offline' | 'unknown';
  reason: string;
  latestRun: ScorecardMonitorRun | null;
};

export type RunScanState = {
  kind: 'scanned' | 'zero-no-active' | 'zero-no-due-or-skipped' | 'zero-skipped-active';
  label: string;
  description: string;
  statusType: 'success' | 'info' | 'warning';
  isZeroScan: boolean;
  isSkippedWithActiveBusinesses: boolean;
};

export type EvidenceFreshness = {
  state: 'fresh' | 'stale' | 'unknown';
  label: string;
  description: string;
  statusType: 'success' | 'warning' | 'info';
};

/** Daily monitor runs are expected once a day (5 AM America/Chicago). A gap of
 * more than this many hours since the last run means monitoring is offline. */
export const OFFLINE_AFTER_HOURS = 30;
export const STUCK_RUN_AFTER_HOURS = 2.5;

function parseUtcTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value.endsWith('Z') ? value : `${value}Z`).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function runStartedAt(run: Pick<ScorecardMonitorRun, 'started_at' | 'created_at'>): string {
  // started_at has a server-side default; fall back to created_at defensively.
  return run.started_at ?? run.created_at;
}

function runDateKey(run: ScorecardMonitorRun): string {
  // started_at is a timezone-unaware UTC datetime string.
  return runStartedAt(run).slice(0, 10);
}

export function classifyMonitorHealth(
  runs: ScorecardMonitorRun[],
  now: Date = new Date(),
  activeMonitoredBusinessCount?: number,
): MonitorHealth {
  if (runs.length === 0) {
    return {
      status: 'unknown',
      reason: 'No monitor runs recorded yet. The daily monitor has never reported in.',
      latestRun: null,
    };
  }
  const sorted = [...runs].sort((a, b) => (runStartedAt(a) < runStartedAt(b) ? 1 : -1));
  const latest = sorted[0];
  const startedMs = parseUtcTimestamp(runStartedAt(latest));
  const ageHours = startedMs === null ? Number.NaN : (now.getTime() - startedMs) / 3_600_000;

  if (Number.isFinite(ageHours) && ageHours > OFFLINE_AFTER_HOURS) {
    return {
      status: 'offline',
      reason: `No monitor run in the last ${Math.floor(ageHours)} hours (expected daily). The monitoring system appears offline.`,
      latestRun: latest,
    };
  }
  if (latest.status === 'failed') {
    return {
      status: 'degraded',
      reason: `The latest monitor run failed${latest.error_message ? `: ${latest.error_message}` : '.'}`,
      latestRun: latest,
    };
  }
  if (['dispatching', 'collecting', 'running'].includes(latest.status)) {
    if (Number.isFinite(ageHours) && ageHours > STUCK_RUN_AFTER_HOURS) {
      return {
        status: 'degraded',
        reason: `The latest monitor run has remained ${latest.status} for ${Math.floor(ageHours)} hours, beyond the source-probe SLO. The watchdog may be unhealthy.`,
        latestRun: latest,
      };
    }
    return {
      status: 'online',
      reason: `A monitor run is currently ${latest.status}; fresh source probes have not finalized yet.`,
      latestRun: latest,
    };
  }
  const scanState = classifyRunScanState(latest, activeMonitoredBusinessCount);
  if (scanState.isSkippedWithActiveBusinesses) {
    return {
      status: 'degraded',
      reason: scanState.description,
      latestRun: latest,
    };
  }
  return {
    status: 'online',
    reason: scanState.isZeroScan ? scanState.description : 'The latest daily monitor run completed.',
    latestRun: latest,
  };
}

export function classifyRunScanState(
  run: ScorecardMonitorRun,
  activeMonitoredBusinessCount?: number,
): RunScanState {
  const scanned = run.businesses_scanned ?? 0;
  if (run.status !== 'completed' || scanned > 0) {
    return {
      kind: 'scanned',
      label: `${scanned} scanned`,
      description: `${scanned} monitored ${scanned === 1 ? 'business was' : 'businesses were'} scanned in this run.`,
      statusType: 'success',
      isZeroScan: false,
      isSkippedWithActiveBusinesses: false,
    };
  }
  if (activeMonitoredBusinessCount === 0) {
    return {
      kind: 'zero-no-active',
      label: '0 scanned · no active businesses',
      description: 'This run completed with 0 businesses scanned because no active monitored businesses are configured.',
      statusType: 'info',
      isZeroScan: true,
      isSkippedWithActiveBusinesses: false,
    };
  }
  const businessesDue = parseRunMetrics(run.metrics_json).businesses_due;
  if (businessesDue === 0) {
    return {
      kind: 'zero-no-due-or-skipped',
      label: '0 scanned · no businesses due',
      description:
        'This run completed with 0 businesses scanned because no active monitored businesses were due on this cadence.',
      statusType: 'info',
      isZeroScan: true,
      isSkippedWithActiveBusinesses: false,
    };
  }
  if (typeof businessesDue === 'number' && businessesDue > 0) {
    return {
      kind: 'zero-skipped-active',
      label: '0 scanned · due scope skipped',
      description: `This run completed with 0 businesses scanned even though ${businessesDue} monitored ${businessesDue === 1 ? 'business was' : 'businesses were'} due. Treat it as a skipped/no-scan state, not healthy monitoring.`,
      statusType: 'warning',
      isZeroScan: true,
      isSkippedWithActiveBusinesses: true,
    };
  }
  if (typeof activeMonitoredBusinessCount === 'number' && activeMonitoredBusinessCount > 0) {
    return {
      kind: 'zero-skipped-active',
      label: '0 scanned · skipped/no-scan',
      description: `This run completed with 0 businesses scanned while ${activeMonitoredBusinessCount} active monitored ${activeMonitoredBusinessCount === 1 ? 'business exists' : 'businesses exist'}. Treat it as a skipped/no-scan state, not healthy monitoring.`,
      statusType: 'warning',
      isZeroScan: true,
      isSkippedWithActiveBusinesses: true,
    };
  }
  return {
    kind: 'zero-no-due-or-skipped',
    label: '0 scanned · no due or skipped',
    description: 'This completed run scanned 0 businesses. That can mean no businesses were due or the run skipped its scope; check the active monitored business count before treating it as healthy.',
    statusType: 'info',
    isZeroScan: true,
    isSkippedWithActiveBusinesses: false,
  };
}

export function evidenceFreshness(
  item: Pick<ScorecardIncident | ScorecardMonitorIssue, 'last_seen_at'>,
  latestRun: Pick<ScorecardMonitorRun, 'started_at' | 'created_at'> | null | undefined,
): EvidenceFreshness {
  const lastSeenMs = parseUtcTimestamp(item.last_seen_at);
  if (lastSeenMs === null) {
    return {
      state: 'unknown',
      label: 'No last verified time',
      description: 'No last collection/verification timestamp is available for this row.',
      statusType: 'info',
    };
  }
  const latestRunMs = latestRun ? parseUtcTimestamp(runStartedAt(latestRun)) : null;
  if (latestRunMs !== null && lastSeenMs < latestRunMs) {
    return {
      state: 'stale',
      label: 'Stale evidence',
      description: 'Last verified/collection attempt is older than the latest monitor run; this row is historical evidence until a newer collection verifies it again.',
      statusType: 'warning',
    };
  }
  return {
    state: 'fresh',
    label: 'Last verified',
    description: 'This row was seen on the latest monitor evidence available to the dashboard.',
    statusType: 'success',
  };
}

export function dailyRunStats(runs: ScorecardMonitorRun[], days = 14): DailyRunStat[] {
  const byDate = new Map<string, DailyRunStat>();
  for (const run of runs) {
    const date = runDateKey(run);
    let stat = byDate.get(date);
    if (!stat) {
      stat = {
        date,
        total: 0,
        completed: 0,
        failed: 0,
        running: 0,
        zeroBusinessCompleted: 0,
        successRate: null,
      };
      byDate.set(date, stat);
    }
    stat.total += 1;
    if (run.status === 'completed') {
      stat.completed += 1;
      if ((run.businesses_scanned ?? 0) === 0) stat.zeroBusinessCompleted += 1;
    } else if (run.status === 'failed') stat.failed += 1;
    else stat.running += 1;
  }
  const stats = [...byDate.values()].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, days);
  for (const stat of stats) {
    const finished = stat.completed + stat.failed;
    stat.successRate = finished > 0 ? stat.completed / finished : null;
  }
  return stats;
}

export function parseRunMetrics(metricsJson: string | null | undefined): RunMetrics {
  const empty: RunMetrics = {
    sources: [],
    overall: null,
    probes: null,
    businesses_due: null,
    recrawls_queued: null,
  };
  if (!metricsJson) return empty;
  let parsed: unknown;
  try {
    parsed = JSON.parse(metricsJson);
  } catch {
    return empty;
  }
  if (!parsed || typeof parsed !== 'object') return empty;
  const record = parsed as Record<string, unknown>;
  const legacySources = new Map<string, Record<string, unknown>>();
  if (record.sources && typeof record.sources === 'object') {
    for (const [source, value] of Object.entries(record.sources as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      legacySources.set(source, value as Record<string, unknown>);
    }
  }
  const sources: SourceTiming[] = [];
  if (record.probe_sources && typeof record.probe_sources === 'object') {
    for (const [source, value] of Object.entries(
      record.probe_sources as Record<string, unknown>,
    )) {
      if (!value || typeof value !== 'object') continue;
      const metrics = value as Record<string, unknown>;
      const legacy = legacySources.get(source);
      sources.push({
        source,
        issues: typeof legacy?.issues === 'number' ? legacy.issues : 0,
        attempted: typeof metrics.attempted === 'number' ? metrics.attempted : 0,
        success: typeof metrics.success === 'number' ? metrics.success : 0,
        failed: typeof metrics.failed === 'number' ? metrics.failed : 0,
        degraded: typeof metrics.degraded === 'number' ? metrics.degraded : 0,
        pending: typeof metrics.pending === 'number' ? metrics.pending : 0,
        duration_ms: typeof metrics.duration_ms === 'number' ? metrics.duration_ms : null,
      });
    }
  } else {
    for (const [source, metrics] of legacySources) {
      sources.push({
        source,
        issues: typeof metrics.issues === 'number' ? metrics.issues : 0,
        attempted: typeof metrics.success === 'number' ? metrics.success : 0,
        success: typeof metrics.success === 'number' ? metrics.success : 0,
        failed: 0,
        degraded: 0,
        pending: 0,
        duration_ms: typeof metrics.duration_ms === 'number' ? metrics.duration_ms : null,
      });
    }
  }
  sources.sort((a, b) => a.source.localeCompare(b.source));
  const overall =
    record.overall && typeof record.overall === 'object'
      ? (record.overall as RunMetrics['overall'])
      : null;
  const probes =
    record.probes && typeof record.probes === 'object'
      ? (record.probes as RunMetrics['probes'])
      : null;
  const dispatch =
    record.dispatch && typeof record.dispatch === 'object'
      ? (record.dispatch as Record<string, unknown>)
      : null;
  const businessesDue =
    typeof dispatch?.businesses_due === 'number'
      ? dispatch.businesses_due
      : typeof record.businesses_selected === 'number'
        ? record.businesses_selected
        : null;
  return {
    sources,
    overall,
    probes,
    businesses_due: businessesDue,
    recrawls_queued: typeof record.recrawls_queued === 'number' ? record.recrawls_queued : null,
  };
}

export function countBy<T>(items: T[], key: (item: T) => string): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of items) {
    const value = key(item);
    counts[value] = (counts[value] ?? 0) + 1;
  }
  return counts;
}

export function issueIndustry(
  issue: ScorecardMonitorIssue,
  industryByReportId: Map<number, string | null | undefined>,
): string {
  if (issue.industry) return issue.industry;
  if (issue.business_type) return issue.business_type;
  if (issue.report_id === null || issue.report_id === undefined) return 'Unknown';
  const industry = industryByReportId.get(issue.report_id);
  return industry || 'Unknown';
}

export function incidentIndustry(
  incident: ScorecardIncident,
  industryByReportId: Map<number, string | null | undefined>,
): string {
  if (incident.industry) return incident.industry;
  if (incident.business_type) return incident.business_type;
  if (incident.report_id === null || incident.report_id === undefined) return 'Unknown';
  const industry = industryByReportId.get(incident.report_id);
  return industry || 'Unknown';
}

export function incidentProvider(incident: ScorecardIncident): string {
  return incident.provider || incident.source || 'Unknown';
}

export function issueProvider(issue: ScorecardMonitorIssue): string {
  return issue.provider || issue.source || 'Unknown';
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `${minutes}m ${remainder}s`;
}

function plural(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const days = Math.floor(safeSeconds / 86_400);
  const hours = Math.floor((safeSeconds % 86_400) / 3_600);
  const minutes = Math.floor((safeSeconds % 3_600) / 60);

  if (days > 0) {
    return hours > 0 ? `${plural(days, 'day')} ${plural(hours, 'hour')}` : plural(days, 'day');
  }
  if (hours > 0) {
    return minutes > 0
      ? `${plural(hours, 'hour')} ${plural(minutes, 'minute')}`
      : plural(hours, 'hour');
  }
  if (minutes > 0) return plural(minutes, 'minute');
  return plural(safeSeconds, 'second');
}

export function formatIncidentDuration(
  incident: Pick<ScorecardIncident, 'duration_seconds' | 'ongoing' | 'state_class' | 'status'>,
): string {
  const duration = formatDurationSeconds(incident.duration_seconds);
  if (duration === '—') return duration;
  if (incident.ongoing) return `${duration}, ongoing`;
  return `${duration}, ${incident.state_class || incident.status || 'resolved'}`;
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${Math.round(value * 100)}%`;
}
